// Pure geometry. Everything in output pixel space unless named otherwise. Unit-tested.

import { PAPERS, type Design, type ImageObject, type Orientation, type PaperId } from './design';

export interface Geometry {
  paper: PaperId;
  shape: 'rect' | 'circle';
  orientation: Orientation;
  trimWidthMm: number;
  trimHeightMm: number;
  bleedMm: number;
  targetDpi: number;
  outW: number;
  outH: number;
  pxPerMm: number;
}

/**
 * Export size: trim + 2 x bleed at target dpi, rounded up, plus one pixel on every side so a
 * circle's arc never touches the canvas edge (rounding 639.4 down to 639 clipped 0.2 px of the
 * 12-up circle and left a visibly flat tangent). The print draws the pixels at their exact size.
 */
export function outputSizePx(trimW: number, trimH: number, bleed: number, dpi: number): [number, number] {
  return [Math.ceil(((trimW + 2 * bleed) / 25.4) * dpi) + 2, Math.ceil(((trimH + 2 * bleed) / 25.4) * dpi) + 2];
}

export function geometry(paper: PaperId, orientation: Orientation, targetDpi: number): Geometry {
  const p = PAPERS[paper];
  let [trimWidthMm, trimHeightMm] = [p.trimWidthMm, p.trimHeightMm];
  if (orientation === 'landscape') [trimWidthMm, trimHeightMm] = [trimHeightMm, trimWidthMm];
  const [outW, outH] = outputSizePx(trimWidthMm, trimHeightMm, p.bleedMm, targetDpi);
  return {
    paper, shape: p.shape as 'rect' | 'circle', orientation, trimWidthMm, trimHeightMm,
    bleedMm: p.bleedMm, targetDpi, outW, outH, pxPerMm: targetDpi / 25.4,
  };
}

/**
 * The first photo: centred and covering the whole canvas with 4 % to spare, so it hangs visibly
 * over the edge instead of ending exactly on it (on a round sheet that looked like a near miss).
 */
export function coverFit(id: string, naturalWidth: number, naturalHeight: number, g: Geometry): ImageObject {
  const scale = 1.04 * Math.max(g.outW / naturalWidth, g.outH / naturalHeight);
  return { id, naturalWidth, naturalHeight, centerX: g.outW / 2, centerY: g.outH / 2, scale, rotation: 0 };
}

/** Any further image: centred, a third of the canvas. */
export function thirdFit(id: string, naturalWidth: number, naturalHeight: number, g: Geometry): ImageObject {
  const scale = Math.min(g.outW / 3 / naturalWidth, g.outH / 3 / naturalHeight);
  return { id, naturalWidth, naturalHeight, centerX: g.outW / 2, centerY: g.outH / 2, scale, rotation: 0 };
}

/** Zoom keeping the output point `at` fixed on screen. */
export function zoomAt(img: ImageObject, factor: number, at: { x: number; y: number }): ImageObject {
  const scale = img.scale * factor;
  return { ...img, scale, centerX: at.x + (img.centerX - at.x) * factor, centerY: at.y + (img.centerY - at.y) * factor };
}

/** With scale = output px per original px, effective dpi = target dpi / scale. */
export function effectiveDpi(img: ImageObject, g: Geometry): number {
  return g.targetDpi / img.scale;
}

/**
 * Whether one image alone covers the whole printed shape (trim + bleed). Only feeds a warning —
 * nothing is clamped. The shape's extreme points are taken into the image's own unrotated frame,
 * where covering is a plain box test. Half a pixel of tolerance absorbs float noise.
 */
export function covers(img: ImageObject, g: Geometry): boolean {
  const hw = (img.naturalWidth * img.scale) / 2 + 0.5, hh = (img.naturalHeight * img.scale) / 2 + 0.5;
  const a = (-img.rotation * Math.PI) / 180, cos = Math.cos(a), sin = Math.sin(a);
  const local = (x: number, y: number) => {
    const dx = x - img.centerX, dy = y - img.centerY;
    return { x: Math.abs(dx * cos - dy * sin), y: Math.abs(dx * sin + dy * cos) };
  };
  const rx = (g.trimWidthMm / 2 + g.bleedMm) * g.pxPerMm, ry = (g.trimHeightMm / 2 + g.bleedMm) * g.pxPerMm;
  const cx = g.outW / 2, cy = g.outH / 2;
  if (g.shape === 'circle') {
    const c = local(cx, cy);
    return c.x + rx <= hw && c.y + rx <= hh;
  }
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].every(([sx, sy]) => {
    const p = local(cx + sx * rx, cy + sy * ry);
    return p.x <= hw && p.y <= hh;
  });
}

/** Stage size that fits the output aspect into a box, and the stage/output ratio. */
export function fitStage(g: Geometry, maxW: number, maxH: number): { width: number; height: number; s: number } {
  const s = Math.min(maxW / g.outW, maxH / g.outH);
  return { width: Math.round(g.outW * s), height: Math.round(g.outH * s), s };
}

/** Objects keep their relative position when the canvas changes size (orientation switch). */
export function rescale<T extends { centerX: number; centerY: number }>(items: T[], from: Geometry, to: Geometry): T[] {
  return items.map((t) => ({ ...t, centerX: (t.centerX / from.outW) * to.outW, centerY: (t.centerY / from.outH) * to.outH }));
}

export function buildDesign(productId: string, g: Geometry, images: ImageObject[], texts: Design['texts']): Design {
  return {
    version: 1, productId, paper: g.paper, shape: g.shape, orientation: g.orientation,
    trimWidthMm: g.trimWidthMm, trimHeightMm: g.trimHeightMm, bleedMm: g.bleedMm, targetDpi: g.targetDpi,
    output: { widthPx: g.outW, heightPx: g.outH }, images,
    effectiveDpi: Math.round(Math.min(...images.map((i) => effectiveDpi(i, g)))), texts,
  };
}
